import { useMemo, useRef } from "react";

type MosaicProps = {
  imageUrls: string[];
  hidden: boolean;
};

// ── Layout templates ──────────────────────────────────────────────────────────
// Each template is a CSS grid-template-areas string + the slot names it uses.
// All templates fill a rectangle; unused slots receive a neutral bg tile.

// Every layout uses exactly 2 rows and no slot spans more than 1 row,
// so photos are always a fixed fraction of the height — never full-height slices.
const LAYOUTS: Array<{ areas: string; slots: string[] }> = [
  // 2-col: uniform 2×2
  {
    areas: `"a b" "c d"`,
    slots: ["a", "b", "c", "d"],
  },
  // 3-col: uniform 2×3
  {
    areas: `"a b c" "d e f"`,
    slots: ["a", "b", "c", "d", "e", "f"],
  },
  // 3-col: wider left top + 3 bottom
  {
    areas: `"a a b" "c d e"`,
    slots: ["a", "b", "c", "d", "e"],
  },
  // 3-col: 3 top + wider right bottom
  {
    areas: `"a b c" "d e e"`,
    slots: ["a", "b", "c", "d", "e"],
  },
  // 4-col: 2 top (each spanning 2 cols) + 4 bottom
  {
    areas: `"a a b b" "c d e f"`,
    slots: ["a", "b", "c", "d", "e", "f"],
  },
  // 4-col: 4 top + 2 bottom (each spanning 2 cols)
  {
    areas: `"a b c d" "e e f f"`,
    slots: ["a", "b", "c", "d", "e", "f"],
  },
  // 4-col: uniform 2×4
  {
    areas: `"a b c d" "e f g h"`,
    slots: ["a", "b", "c", "d", "e", "f", "g", "h"],
  },
];

function seededShuffle<T>(arr: T[], seed: number): T[] {
  const out = [...arr];
  let s = seed;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    const j = Math.abs(s) % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export const RichLauncherMosaic = ({ imageUrls, hidden }: MosaicProps) => {
  // Generate a random seed once on mount so the layout and photo order are
  // different on every page load, while remaining stable across re-renders.
  const seedRef = useRef<number | null>(null);
  if (seedRef.current === null) {
    seedRef.current = Math.floor(Math.random() * 0xffffffff);
  }
  const seed = seedRef.current;

  const { layout, shuffledUrls } = useMemo(() => {
    const layoutIndex = Math.abs(seed) % LAYOUTS.length;
    const chosen = LAYOUTS[layoutIndex];
    const shuffled = seededShuffle(imageUrls, seed);
    return { layout: chosen, shuffledUrls: shuffled };
  }, [imageUrls, seed]);

  const colCount = layout.areas.split('"')[1].trim().split(/\s+/).length;

  return (
    <div
      className={`tripper-rich-launcher__mosaic${hidden ? " tripper-rich-launcher__mosaic--hidden" : ""}`}
      style={{
        gridTemplateAreas: layout.areas,
        gridTemplateColumns: `repeat(${colCount}, 1fr)`,
      }}
    >
      {layout.slots.map((slot, i) => {
        const url = shuffledUrls[i] ?? null;
        return (
          <div
            key={slot}
            className="tripper-rich-launcher__mosaic-cell"
            style={{ gridArea: slot }}
          >
            {url ? (
              <img
                className="tripper-rich-launcher__mosaic-img"
                src={url}
                alt=""
                loading="eager"
                draggable={false}
              />
            ) : (
              <div className="tripper-rich-launcher__mosaic-placeholder" />
            )}
          </div>
        );
      })}
    </div>
  );
};
